import { Measurement } from "@cur8/rich-entity";
import { DateTime } from "luxon";
import { MetricConfig } from "./measurementConfig";
import { ConversionDef } from "./measurementConversion";

export function formatMeasurementValue(
  config: MetricConfig,
  measurement: Measurement | null | undefined
): string | undefined {
  if (!measurement) {
    return undefined;
  }

  const values = config.dimensions
    .map((dimConfig) => {
      const dim = measurement.dimensions.find((d) => d.name === dimConfig.name);
      return dim == null ? null : { value: dim.value, unit: dimConfig.unit };
    })
    .filter((v): v is { value: number; unit: string } => v != null);

  if (values.length === 0) {
    return undefined;
  }

  // Same-unit dimensions (e.g. systolic/diastolic) collapse to standard "120/80 mm Hg" notation.
  const sharedUnit = values.every((v) => v.unit === values[0].unit)
    ? values[0].unit
    : null;

  if (sharedUnit != null) {
    return `${values.map((v) => v.value).join("/")} ${sharedUnit}`;
  }

  return values.map((v) => `${v.value} ${v.unit}`).join(" / ");
}

// Formats a measurement for display in whichever unit the operator currently
// has selected (imperial conversion when active), rather than always in the
// config's base/SI unit — so a value shown next to an lbs input reads in lbs.
export function formatPreviousValue(
  config: MetricConfig,
  measurement: Measurement | null | undefined,
  convDef: ConversionDef | null
): string | undefined {
  if (!measurement) {
    return undefined;
  }
  if (!convDef) {
    return formatMeasurementValue(config, measurement);
  }

  // Imperial conversions only apply to single-dimension metrics (see
  // measurementConfig.ts), so there's exactly one SI dimension to convert.
  const siValue = measurement.dimensions.find(
    (d) => d.name === config.dimensions[0].name
  )?.value;
  if (siValue == null) {
    return undefined;
  }

  const displayValues = convDef.toDisplay(siValue);
  return convDef.fields
    .map((field, i) => `${displayValues[i]} ${field.unit}`)
    .join(" ");
}

// US convention writes month before day ("July 20, 2026"); everywhere else
// keeps the existing day-first form ("20 July 2026").
export function formatPreviousDate(
  date: DateTime,
  countryCode: string | undefined
): string {
  return countryCode === "US"
    ? date.toFormat("MMMM d, yyyy")
    : date.toFormat("d MMMM yyyy");
}

// Placeholder shown before a metric has been measured, e.g. "kg" or "mm Hg"
// for same-unit dimension pairs like systolic/diastolic, rather than "mm Hg / mm Hg".
export function formatUnitPlaceholder(config: MetricConfig): string {
  const units = config.dimensions.map((d) => d.unit);
  const sharedUnit = units.every((u) => u === units[0]) ? units[0] : null;
  return sharedUnit ?? units.join(" / ");
}
